import React, { useState } from "react";
import {
  Inbox,
  ListTree,
  Loader2,
  PanelLeft,
  RefreshCw,
  Search,
} from "lucide-react";

const getPermissionId = (permission) => {
  return String(
    permission?.pk_permission_id || permission?.permission_id || "",
  );
};

const getPermissionName = (permission) => {
  return (
    permission?.permission_name || permission?.permissionName || "Permission"
  );
};

const getRoleId = (role) => {
  return String(role?.pk_role_id || role?.role_id || role?.id || "");
};

const getRoleName = (role) => {
  const rawRole =
    role?.role ||
    role?.roleName ||
    role?.role_name ||
    "Unnamed Role";

  return String(rawRole).split("|")[0]?.trim() || "Unnamed Role";
};

const getAdminId = (admin) => {
  if (!admin || typeof admin !== "object") return "";

  const id =
    admin.pk_admin_id ??
    admin.admin_id ??
    admin.adminId ??
    admin.id ??
    admin.admin_loginid ??
    admin.userId;

  return id === null || id === undefined ? "" : String(id).trim();
};

const getAdminName = (admin) => {
  const firstName = admin?.first_name || admin?.firstName || "";
  const lastName = admin?.last_name || admin?.lastName || "";
  const fullName = `${firstName} ${lastName}`.trim();

  return admin?.admin_username || fullName || "Unnamed Admin";
};

const getAdminLoginId = (admin) => {
  return String(admin?.admin_loginid || admin?.userId || "");
};

const getRoleNameById = (roles = [], roleId = "") => {
  const role = roles.find((item) => String(getRoleId(item)) === String(roleId));
  return role ? getRoleName(role) : "No Role";
};

const getSidebarAccessId = (item) => {
  return String(item?.id || item?.pk_sidebar_id || "");
};

const getMenuId = (item) => {
  return String(item?.pk_menu_id || item?.menuId || "");
};

const getSubmenuId = (item) => {
  return String(item?.pk_submenu_id || item?.submenuId || item?.id || "");
};

const getMenuTitle = (item) => {
  return item?.menu_title || item?.menuTitle || "Untitled Menu";
};

const getSubmenuTitle = (item) => {
  return (
    item?.submenu_title || item?.submenuTitle || item?.menu_title || "Untitled"
  );
};

const getMenuStatus = (item) => {
  return (
    item?.status === "Y" || item?.is_active === "Y" || item?.is_active === 1
  );
};

const buildAccessKey = (item) => {
  return `${getMenuId(item)}_${getSubmenuId(item)}`;
};

const buildSidebarSelectionKey = (item) => {
  if (!item || typeof item !== "object") {
    return "";
  }

  const menuId = getMenuId(item);
  const submenuId = getSubmenuId(item);
  const itemId = String(item?.id || "");
  const url = String(item?.submenu_url || item?.menu_url || "");

  return [
    menuId || "menu",
    submenuId || itemId || "submenu",
    url || "no-url",
  ].join("_");
};

const buildAccessManagementKey = (item) => {
  return `${item?.permissionId}_${item?.menuId}_${item?.submenuId}`;
};

const groupPermissionsByModule = (permissions = []) => {
  return permissions.reduce((grouped, permission) => {
    const moduleName = permission?.module_name || "Other Permissions";

    if (!grouped[moduleName]) {
      grouped[moduleName] = [];
    }

    grouped[moduleName].push(permission);
    return grouped;
  }, {});
};

const buildRolePermissionRowMap = (rolePermissions = []) => {
  const safeRolePermissions = Array.isArray(rolePermissions)
    ? rolePermissions
    : [];

  return safeRolePermissions.reduce((map, item) => {
    if (!item || typeof item !== "object") return map;

    const permissionId =
      item.permission_id === null || item.permission_id === undefined
        ? ""
        : String(item.permission_id);

    if (!permissionId) return map;

    map[permissionId] = item;
    return map;
  }, {});
};

const buildActivePermissionIdSet = (rolePermissions = []) => {
  const safeRolePermissions = Array.isArray(rolePermissions)
    ? rolePermissions
    : [];

  return new Set(
    safeRolePermissions
      .filter(
        (item) =>
          item &&
          typeof item === "object" &&
          (item.is_active === "Y" ||
            item.is_active === 1 ||
            item.is_active === "1"),
      )
      .map((item) => item?.permission_id)
      .filter((id) => id !== null && id !== undefined && id !== "")
      .map(String),
  );
};

const flattenSidebarAccess = (accessList = []) => {
  const result = [];

  accessList.forEach((menu) => {
    const submenus = Array.isArray(menu?.submenus) ? menu.submenus : [];

    if (submenus.length === 0) {
      result.push({
        id: getSidebarAccessId(menu),
        pk_menu_id: menu?.pk_menu_id,
        pk_submenu_id: menu?.pk_submenu_id,
        menu_title: menu?.menu_title,
        menu_icon: menu?.menu_icon,
        menu_url: menu?.menu_url,
        menu_type: menu?.menu_type,
        status: menu?.status,
        submenu_title: menu?.menu_title,
        submenu_icon: menu?.menu_icon,
        submenu_url: menu?.menu_url,
      });
      return;
    }

    submenus.forEach((submenu) => {
      result.push({
        id: submenu?.id || menu?.id,
        pk_menu_id: menu?.pk_menu_id,
        pk_submenu_id: submenu?.id || menu?.pk_submenu_id,
        menu_title: menu?.menu_title,
        menu_icon: menu?.menu_icon,
        menu_url: menu?.menu_url,
        menu_type: menu?.menu_type,
        status: submenu?.status || menu?.status,
        submenu_title: submenu?.submenu_title || menu?.menu_title,
        submenu_icon: submenu?.submenu_icon || menu?.menu_icon,
        submenu_url: submenu?.submenu_url || menu?.menu_url,
      });
    });
  });

  return result;
};

const buildUserAccessMap = (userAccessList = []) => {
  const flatUserAccess = flattenSidebarAccess(userAccessList);

  return flatUserAccess.reduce((map, item) => {
    const key = buildAccessKey(item);
    map[key] = item;
    return map;
  }, {});
};

const searchPermissions = (permissions = [], searchText = "") => {
  const safePermissions = Array.isArray(permissions) ? permissions : [];
  const query = normalizeSearchValue(searchText).trim();

  if (!query) return safePermissions;

  return safePermissions.filter((permission) => {
    if (!permission || typeof permission !== "object") return false;

    return [
      permission?.permission_name,
      permission?.permissionName,
      permission?.module_name,
      permission?.pk_permission_id,
      permission?.permission_id,
    ].some((value) => normalizeSearchValue(value).includes(query));
  });
};

const searchAdmins = (admins = [], searchText = "") => {
  const safeAdmins = Array.isArray(admins) ? admins : [];
  const query = normalizeSearchValue(searchText).trim();

  if (!query) return safeAdmins;

  return safeAdmins.filter((admin) => {
    if (!admin || typeof admin !== "object") {
      return false;
    }

    return [
      getAdminName(admin),
      getAdminLoginId(admin),
      getAdminId(admin),
      admin?.first_name,
      admin?.last_name,
      admin?.admin_username,
    ].some((value) => normalizeSearchValue(value).includes(query));
  });
};

const normalizeSearchValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).toLowerCase();
};

const searchSidebarAccess = (accessList = [], searchText = "") => {
  const safeList = Array.isArray(accessList) ? accessList : [];
  const query = normalizeSearchValue(searchText).trim();

  if (!query) return safeList;

  return safeList.filter((item) => {
    if (!item || typeof item !== "object") return false;

    return [
      item?.menu_title,
      item?.submenu_title,
      item?.menu_url,
      item?.submenu_url,
      item?.id,
      item?.pk_menu_id,
      item?.pk_submenu_id,
    ].some((value) => normalizeSearchValue(value).includes(query));
  });
};

const getModuleCount = (permissions = []) => {
  return Object.keys(groupPermissionsByModule(permissions)).length;
};

const InlineLoader = ({ text = "Loading...", className = "" }) => {
  return (
    <div
      className={`flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500 ${className}`}
    >
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>{text}</span>
    </div>
  );
};

const EmptyState = ({
  title = "No data found",
  description = "Nothing to show here.",
}) => {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
      <div className="mb-3 rounded-full bg-white p-3 shadow-sm">
        <Inbox className="h-6 w-6 text-slate-400" />
      </div>
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      <p className="mt-1 max-w-sm text-xs text-slate-500">{description}</p>
    </div>
  );
};

const SidebarAccessManager = ({
  admins = [],
  allSidebarAccess = [],
  selectedUserAccess = [],
  selectedAccessAdminId = "",
  adminsLoading = false,
  userAccessLoading = false,
  sideBarUpdateLoading = false,
  onSelectedAccessAdminChange,
  onLoadAllAccess,
  onLoadUserAccess,
  onToggleSidebarStatus,
}) => {
  const [searchText, setSearchText] = useState("");

  const flatAllAccess = flattenSidebarAccess(allSidebarAccess);
  const userAccessMap = buildUserAccessMap(selectedUserAccess);
  const filteredAccess = searchSidebarAccess(flatAllAccess, searchText);

  const selectedAdmin = admins.find(
    (admin) => String(getAdminId(admin)) === String(selectedAccessAdminId),
  );

  const handleAdminChange = async (adminId) => {
    onSelectedAccessAdminChange(adminId);

    if (adminId) {
      await onLoadUserAccess(adminId);
    }
  };

  const handleReload = async () => {
    await onLoadAllAccess();

    if (selectedAccessAdminId) {
      await onLoadUserAccess(selectedAccessAdminId);
    }
  };

  return (
    <div className="grid h-full min-h-0 min-w-0 grid-rows-[auto_minmax(0,1fr)] gap-4 overflow-hidden">
      <div className="shrink-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-orange-50 p-2.5 text-orange-600">
              <PanelLeft className="h-5 w-5" />
            </div>

            <div>
              <h2 className="text-base font-bold text-slate-900">
                User Sidebar Access
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Select admin from the same admin list and map their assigned
                sidebar access on top of all sidebar menus.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleReload}
            disabled={userAccessLoading}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            <RefreshCw
              className={`h-4 w-4 ${userAccessLoading ? "animate-spin" : ""}`}
            />
            Reload Access
          </button>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1fr]">
          <div>
            <label className="mb-1.5 block text-xs font-bold text-slate-600">
              Select Admin
            </label>

            <select
              value={selectedAccessAdminId}
              onChange={(event) => handleAdminChange(event.target.value)}
              disabled={adminsLoading || userAccessLoading}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100 disabled:opacity-60"
            >
              <option value="">Select admin to view access</option>

              {admins.map((admin) => (
                <option key={getAdminId(admin)} value={getAdminId(admin)}>
                  {getAdminName(admin)} - {getAdminLoginId(admin)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-bold text-slate-600">
              Search Sidebar
            </label>

            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search menu, submenu or URL..."
                className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-3 text-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <b>Flow:</b> getUserAccess blank loads all sidebar menus. After
          selecting admin, getUserAccess(adminId) loads only that admin access.
          Then UI maps by menuId + submenuId and shows ON/OFF.
        </div>
      </div>

      {!selectedAccessAdminId ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <EmptyState
            title="Select admin"
            description="Select an admin to see which sidebar menus are assigned to them."
          />
        </div>
      ) : userAccessLoading ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <InlineLoader text="Loading sidebar access..." />
        </div>
      ) : filteredAccess.length === 0 ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <EmptyState
            title="No sidebar access found"
            description="No sidebar menu found from getUserAccess blank API."
          />
        </div>
      ) : (
        <div className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-2 border-b border-slate-100 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-base font-bold text-slate-900">
                Sidebar Mapping
              </h3>

              <p className="text-sm text-slate-500">
                Showing mapped access for{" "}
                <b>
                  {selectedAdmin
                    ? getAdminName(selectedAdmin)
                    : "selected admin"}
                </b>
              </p>
            </div>

            <div className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600">
              Total Sidebar Items: {filteredAccess.length}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
            <table className="w-full min-w-[900px] divide-y divide-slate-200 text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 shadow-sm">
                <tr>
                  <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                    Menu
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                    Submenu
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                    URL
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                    Access
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-slate-500">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredAccess.map((item) => {
                  const accessKey = buildAccessKey(item);
                  const mappedUserAccess = userAccessMap[accessKey];

                  const hasAccess = Boolean(mappedUserAccess);
                  const currentStatus =
                    hasAccess && getMenuStatus(mappedUserAccess);

                  const updateId =
                    mappedUserAccess?.id ||
                    mappedUserAccess?.pk_sidebar_id ||
                    item?.id;

                  return (
                    <tr
                      key={`${accessKey}_${item?.id}`}
                      className="hover:bg-slate-50"
                    >
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="rounded-lg bg-orange-50 p-2 text-orange-600">
                            <ListTree className="h-4 w-4" />
                          </div>

                          <div>
                            <p className="font-bold text-slate-900">
                              {item?.menu_title || "Untitled"}
                            </p>
                            <p className="text-xs text-slate-400">
                              Menu ID: {getMenuId(item)}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        <p className="font-medium text-slate-700">
                          {item?.submenu_title ||
                            item?.menu_title ||
                            "Untitled"}
                        </p>
                        <p className="text-xs text-slate-400">
                          Submenu ID: {getSubmenuId(item)}
                        </p>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-slate-500">
                        {item?.submenu_url || item?.menu_url || "-"}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        {currentStatus ? (
                          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
                            Access ON
                          </span>
                        ) : (
                          <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-bold text-rose-700">
                            Access OFF
                          </span>
                        )}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button
                          type="button"
                          disabled={sideBarUpdateLoading || !updateId}
                          onClick={() =>
                            onToggleSidebarStatus(
                              updateId,
                              currentStatus ? "N" : "Y",
                            )
                          }
                          className={`inline-flex items-center justify-center rounded-xl px-4 py-2 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-60 ${
                            currentStatus
                              ? "bg-rose-50 text-rose-700 hover:bg-rose-100"
                              : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          }`}
                        >
                          {currentStatus ? "Turn OFF" : "Turn ON"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default function SidebarAccessTab(props) {
  return (
    <div className="h-full min-h-0 overflow-hidden">
      <SidebarAccessManager {...props} />
    </div>
  );
}
